import React, { useState } from "react";
import { Share2, Copy, Check, Facebook, Twitter, Linkedin, MessageCircle, Mail } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

const SITE_URL = "https://tradingox.us";
const SHARE_TEXT = "TradingoX — AI-powered stock trading, made simple. Buy every dip, sell every rip. Free to join, only 1.25% on profits.";

export default function ShareBar() {
  const [copied, setCopied] = useState(false);

  const url = SITE_URL;
  const text = SHARE_TEXT;
  const enc = encodeURIComponent;

  const openShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "TradingoX", text, url });
        return true;
      } catch (err) {
        console.debug("[Share] native share cancelled", err);
      }
    }
    return false;
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      console.debug("[Share] copy failed", err);
    }
  };

  const links = [
    { key: "fb", label: "Facebook", icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}` },
    { key: "tw", label: "X / Twitter", icon: Twitter, href: `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}` },
    { key: "li", label: "LinkedIn", icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}` },
    { key: "wa", label: "WhatsApp", icon: MessageCircle, href: `https://api.whatsapp.com/send?text=${enc(text + " " + url)}` },
    { key: "em", label: "Email", icon: Mail, href: `mailto:?subject=${enc("Check out TradingoX")}&body=${enc(text + "\n\n" + url)}` },
  ];

  return (
    <div
      data-testid="share-bar"
      className="relative z-10 border-b border-white/5 bg-gradient-to-r from-emerald-500/10 via-transparent to-emerald-500/10 backdrop-blur-md"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-2.5 flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-2 text-center sm:text-left">
        <p className="text-[12px] sm:text-[13px] text-slate-300">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-300 mr-2">love this?</span>
          <span className="hidden sm:inline">Help a friend trade smarter — </span>share TradingoX in one tap.
        </p>
        <div className="flex items-center gap-2">
          <button
            data-testid="share-native-btn"
            onClick={async () => {
              const ok = await openShare();
              if (!ok) copyLink();
            }}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black text-[12px] font-bold transition"
          >
            <Share2 className="w-3.5 h-3.5" /> Share
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="share-more-btn"
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/25 text-slate-200 text-[12px] font-semibold transition"
              >
                More
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              sideOffset={8}
              className="w-52 bg-[#0F111A]/95 backdrop-blur-md border border-white/10 text-slate-200"
            >
              <DropdownMenuItem
                data-testid="share-copy-link"
                onSelect={(e) => { e.preventDefault(); copyLink(); }}
                className="cursor-pointer text-[13px] flex items-center gap-2"
              >
                {copied
                  ? <><Check className="w-4 h-4 text-emerald-300" /> Copied!</>
                  : <><Copy className="w-4 h-4 text-emerald-300" /> Copy link</>}
              </DropdownMenuItem>
              {links.map((l) => (
                <DropdownMenuItem asChild key={l.key}>
                  <a
                    href={l.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`share-${l.key}`}
                    className="cursor-pointer text-[13px] flex items-center gap-2"
                  >
                    <l.icon className="w-4 h-4 text-emerald-300" /> {l.label}
                  </a>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}
